import { useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Sparkles, Type, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

type Tab = 'text' | 'multi-image';
type Status = 'idle' | 'pending';

interface Props {
  open: boolean;
  isPending?: boolean;
  onClose: () => void;
  /**
   * Called when user hits Generate. Caller dispatches the right mutation
   * (text-to-3d OR multi-image-to-3d) and routes to AvatarDetail on success.
   */
  onGenerateText: (textPrompt: string, title?: string) => Promise<void>;
  onGenerateMultiImage: (files: File[], title?: string) => Promise<void>;
}

const MAX_PROMPT_CHARS = 600;

/**
 * Phase 5+ — Direct 3D creation modal. Two tabs:
 *   - "From text"        → Meshy text-to-3d  ($0.40)
 *   - "From photos"      → Meshy multi-image-to-3d (1-4 photos, $0.60)
 *
 * Skips the Gemini 2D step entirely.
 */
export function Direct3dModal({ open, isPending, onClose, onGenerateText, onGenerateMultiImage }: Props) {
  const haptics = useHaptics();
  const [tab, setTab] = useState<Tab>('text');
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const trimmedPrompt = prompt.trim();
  const canSubmitText = trimmedPrompt.length >= 3 && trimmedPrompt.length <= MAX_PROMPT_CHARS && !isPending && status === 'idle';
  const canSubmitMulti = files.length >= 1 && files.length <= 4 && !isPending && status === 'idle';

  const handleGenerate = async () => {
    haptics.impact('medium');
    setStatus('pending');
    try {
      if (tab === 'text' && canSubmitText) {
        await onGenerateText(trimmedPrompt, title.trim() || undefined);
      } else if (tab === 'multi-image' && canSubmitMulti) {
        await onGenerateMultiImage(files, title.trim() || undefined);
      }
    } finally {
      setStatus('idle');
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(e.target.files ?? []).slice(0, 4);
    setFiles(next);
    haptics.selection();
  };

  const removeFile = (i: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
    haptics.selection();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">Create avatar in 3D</h1>
          <p className="mt-0.5 text-xs text-fg-muted">Skip the 2D step. Direct to mesh.</p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={() => { haptics.impact('light'); onClose(); }}
          className="rounded-full bg-bg-secondary p-2 text-fg transition-opacity active:opacity-80"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Tab strip */}
      <div className="mx-4 mb-3 flex gap-1 rounded-full bg-bg-secondary p-1">
        {(['text', 'multi-image'] as Tab[]).map(t => {
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => { haptics.selection(); setTab(t); }}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors',
                active ? 'bg-brand text-brand-fg' : 'text-fg-muted',
              )}
            >
              {t === 'text' ? <Type aria-hidden className="h-3.5 w-3.5" /> : <ImageIcon aria-hidden className="h-3.5 w-3.5" />}
              {t === 'text' ? 'From text' : 'From photos'}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          {/* Common title field */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="d3d-title">Title (optional)</label>
            <input
              id="d3d-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={64}
              placeholder="Give your avatar a name"
              className="rounded-xl bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
          </div>

          {tab === 'text' ? (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="d3d-prompt">Prompt</label>
                <textarea
                  id="d3d-prompt"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  maxLength={MAX_PROMPT_CHARS}
                  placeholder="A heroic samurai in obsidian armor, neon underglow"
                  className="min-h-[140px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
                />
                <p className="text-right text-[11px] text-fg-muted">{trimmedPrompt.length} / {MAX_PROMPT_CHARS}</p>
              </div>
              <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
                <p className="font-semibold text-fg">Quick text → 3D</p>
                <p className="mt-1">Meshy generates the mesh directly from your prompt. ~$0.40, 2-3 min. The preview thumbnail is generated automatically.</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase text-fg-muted">Photos (1-4)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-fg-muted/40 bg-bg-secondary px-3 py-3 text-sm text-fg-muted"
                >
                  <ImageIcon aria-hidden className="h-4 w-4" />
                  {files.length > 0 ? `${files.length} / 4 selected — tap to change` : 'Tap to select 1-4 photos'}
                </button>
                {files.length > 0 && (
                  <ul className="flex flex-col gap-1">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-bg-secondary px-3 py-1.5 text-xs">
                        <span className="line-clamp-1 text-fg">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label="Remove"
                          className="rounded-full p-1 text-fg-muted active:opacity-80"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
                <p className="font-semibold text-fg">Deluxe multi-photo → 3D</p>
                <p className="mt-1">Best results with 3-4 photos of the SAME subject from different angles (front, side, back). ~$0.60, 3-5 min.</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={tab === 'text' ? !canSubmitText : !canSubmitMulti}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-opacity active:opacity-80',
            (tab === 'text' ? canSubmitText : canSubmitMulti) ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
          )}
        >
          {status === 'pending' || isPending ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles aria-hidden className="h-4 w-4" />
          )}
          {tab === 'text' ? 'Generate 3D · ~$0.40' : `Generate 3D · ~$0.60`}
        </button>
      </div>
    </div>
  );
}
